import React, { useMemo } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

import { useAuth } from "hooks";
import AppSwitch from "components/Shared/AppSwitch";
import { getDateTimeAuto } from "utils/dateFormats";
import { useListGlobalListQuery } from "store/services/globalListApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  sourceModel: "",
  sourceModelRef: "",
  description: "",
  collectionMethod: "",
  bankName: null,
  reference: "",
  amount: "",
  isActive: true,
};

const PAYMENT_SOURCE_MODEL = {
  VENDOR_PROFILE: "Vendor Profile",
  CUSTOMER_PROFILE: "Customer Profile",
  CONTRACTOR_PROFILE: "Contractor Profile",
};

const PrepaymentForm = ({
  isBusy = null,
  prepaymentDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  // Watch form fields
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchSourceModel = useWatch({ control, name: "sourceModel" });

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const formattedBankNameOptions = React.useMemo(
    () =>
      bankNameOptions?.map((option) => ({
        value: option._id,
        label: option.name,
      })) || [],
    [bankNameOptions]
  );
  const { data: entityTypeList = [] } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );
  // Memoize mapped lists
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
            label: item?.name || "",
            value: item?._id || "",
          }))
        : [],
    [entityTypeList]
  );

  const _watchCollectionMethod = useWatch({
    control,
    name: "collectionMethod",
  });
  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchCollectionMethod) return null;
    return entityTypeList.find((item) => item._id === _watchCollectionMethod);
  }, [_watchCollectionMethod, entityTypeList]);

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();

  const [fetchCustomers, { data: customers, isLoading: loadingCustomers }] =
    useLazyGetDDLCustomerQuery();

  const [
    fetchContractors,
    { data: contractors, isLoading: loadingContractors },
  ] = useLazyGetDDLContractorListQuery();

  // Dynamic label for sourceModelRef
  const sourceRefLabel = (() => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return "Vendor Profile";
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return "Customer Profile";
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return "Contractor Profile";
      default:
        return "Source Profile";
    }
  })();

  // Dynamic options for sourceModelRef
  const sourceRefOptions = React.useMemo(() => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return {
          data: vendors || [],
          getOptionLabel: (option) =>
            option ? `${option?.shopName} - ${option?.ownerName}` : "",
          isLoading: loadingVendors,
        };
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return {
          data: customers || [],
          getOptionLabel: (option) =>
            option ? `${option?.customerUser?.name} - ${option?.phone}` : "",
          isLoading: loadingCustomers,
        };
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return {
          data: contractors || [],
          getOptionLabel: (option) =>
            option ? `${option?.name} - ${option?.phone}` : "",
          isLoading: loadingContractors,
        };
      default:
        return { data: [], getOptionLabel: () => "", isLoading: false };
    }
  }, [
    _watchSourceModel,
    vendors,
    customers,
    contractors,
    loadingVendors,
    loadingCustomers,
    loadingContractors,
  ]);

  // Methods
  const handleOnPrepaymentSubmit = (data) => {
    const _prepaymentPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      bankName:
        selectedCollectionMethod?.name === "Bank" ? data.bankName : null,
      sourceModelRef: data?.sourceModelRef?._id,
      sourceModel:
        data?.sourceModel === PAYMENT_SOURCE_MODEL.VENDOR_PROFILE
          ? "VendorProfile"
          : data?.sourceModel === PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE
          ? "Customer"
          : "Contractor",
      amount: Number(data?.amount),
      paymentAccount: data?.paymentAccount?._id,
    };
    onSubmit(_prepaymentPayload);
  };

  // Reset form when editing
  React.useEffect(() => {
    if (!prepaymentDetail || loadingBankNameOptions) return;
    const bankNameValue = prepaymentDetail.bankName?._id
      ? formattedBankNameOptions.find(
          (option) => option.value === prepaymentDetail.bankName._id
        )?.value || ""
      : "";
    reset({
      ...prepaymentDetail,
      prepaymentDate: prepaymentDetail?.prepaymentDate
        ? new Date(prepaymentDetail.prepaymentDate).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      sourceModel:
        prepaymentDetail?.sourceModel === "VendorProfile"
          ? PAYMENT_SOURCE_MODEL.VENDOR_PROFILE
          : prepaymentDetail?.sourceModel === "Customer"
          ? PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE
          : PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE,
      sourceModelRef: prepaymentDetail?.sourceModelRef,
      collectionMethod: prepaymentDetail.collectionMethod || "",
      bankName: bankNameValue,
      amount: prepaymentDetail?.amount?.toString(),
      paymentAccount: prepaymentDetail?.paymentAccount || "",
    });
  }, [
    reset,
    loadingBankNameOptions,
    formattedBankNameOptions,
    prepaymentDetail,
  ]);

  // Fetch entities when account changes
  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (selectedCollectionMethod !== "Bank") {
      setValue("bankName", null);
    }
  }, [setValue, selectedCollectionMethod]);

  // Fetch profiles based on sourceModel
  React.useEffect(() => {
    if (!_watchEntity?._id || !_watchAccount?._id || !_watchSourceModel) return;
    if (_watchSourceModel === PAYMENT_SOURCE_MODEL.VENDOR_PROFILE) {
      fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE) {
      fetchCustomers({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE) {
      fetchContractors({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
    }
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchSourceModel,
    fetchVendors,
    fetchCustomers,
    fetchContractors,
  ]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("sourceModelRef", "");
                      setValue("paymentAccount", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("sourceModelRef", "");
                      setValue("paymentAccount", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Source Model */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="sourceModel"
              control={control}
              rules={{ required: "Please select a source model." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  required
                  label="Source Model"
                  options={Object.values(PAYMENT_SOURCE_MODEL)}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue("sourceModelRef", "");
                    setValue("paymentAccount", "");
                  }}
                  helperText={errors?.sourceModel?.message}
                  error={!!errors?.sourceModel}
                />
              )}
            />
          </Grid>

          {/* Source Model Reference */}
          {_watchSourceModel && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="sourceModelRef"
                control={control}
                rules={{
                  required: `Please select a ${sourceRefLabel.toLowerCase()}.`,
                }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label={sourceRefLabel}
                    required
                    value={value || ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("paymentAccount", "");
                    }}
                    getOptionLabel={sourceRefOptions.getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={sourceRefOptions.data}
                    loading={sourceRefOptions.isLoading}
                    loadingText={`Loading ${sourceRefLabel}...`}
                    helperText={errors?.sourceModelRef?.message}
                    error={!!errors?.sourceModelRef}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="collectionMethod"
              control={control}
              rules={{ required: "Please select a collection method." }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  label="Collection Mode"
                  options={_mappedAccountTypeList}
                  // value={field.value || ""}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors.collectionMethod}
                  helperText={errors.collectionMethod?.message}
                />
              )}
            />
          </Grid>

          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item xs={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Collection Method is required" }}
                render={({ field }) => (
                  <BaseSelect
                    fullWidth
                    label="Bank Name"
                    options={formattedBankNameOptions}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionValue={(option) => option.value || ""}
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                  />
                )}
              />
            </Grid>
          )}

          {/* Amount */}
          <Grid item xs={6}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: "Amount is required",
                pattern: {
                  value: /^[0-9]+(\.[0-9]{1,2})?$/,
                  message: "Please enter a valid amount",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  error={!!errors?.amount}
                  helperText={errors?.amount?.message}
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? "" : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>

          {/* Reference */}
          <Grid item xs={6}>
            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Reference"
                  error={!!errors?.reference}
                  helperText={errors?.reference?.message}
                />
              )}
            />
          </Grid>
          {/* Description */}
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Delivery Notes"
                  size="small"
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Is Active */}
          {prepaymentDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnPrepaymentSubmit)}
        >
          {prepaymentDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default PrepaymentForm;
